import type { HDPEComposition, SpecificationListItem } from 'api';
import { Operand } from 'api';
import { translate } from 'i18n';
import type { ComponentProps } from 'react';

import { MaterialBreakdownItem } from '../../components/MaterialBreakdownItem/MaterialBreakdownItem';
import { formatPercentageFeature } from '../formatFeature/formatFeature';
import { formatInstructionWarning } from '../formatInstructionWarning/formatInstructionWarning';

type MaterialItemDisplayData = {
  rawValue: number;
  value: string;
  warning?: string;
  highlight?: boolean;
};

type CompositionItem = ComponentProps<typeof MaterialBreakdownItem>;

const shouldShowItem = (item: MaterialItemDisplayData | null | undefined) => {
  if (!item) {
    return false;
  }

  if (item.warning || item.highlight) {
    return true;
  }

  return item.rawValue !== 0;
};

const buildMaterialItemDisplayData = (
  value: number | null | undefined,
  operand: Operand,
  instructions?: SpecificationListItem[]
): MaterialItemDisplayData | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const instruction = formatInstructionWarning(operand, instructions);
  return {
    highlight: instruction?.highlight ?? false,
    rawValue: value,
    value: formatPercentageFeature(value),
    warning: instruction?.text,
  };
};

const getTotalHdpe = (composition: HDPEComposition) =>
  composition.hdpe_natural_food_grade +
  composition.hdpe_natural_non_food_grade +
  composition.hdpe_white_food_grade +
  composition.hdpe_white_non_food_grade +
  composition.hdpe_jazz +
  composition.hdpe_silicon_tubes;

const getTotalNonHdpe = (composition: HDPEComposition) =>
  composition.expanded_polystyrene +
  composition.pet +
  composition.other_plastic +
  composition.metal +
  composition.waste +
  composition.prohibitive;

export const buildHDPECompositionItems = (composition: HDPEComposition, instructions?: SpecificationListItem[]) => {
  const totalHdpe = getTotalHdpe(composition);
  const totalNonHdpe = getTotalNonHdpe(composition);

  const componentDisplayData = {
    expandedPolystyrene: buildMaterialItemDisplayData(
      composition.expanded_polystyrene,
      Operand.expanded_polystyrene,
      instructions
    ),
    hdpeJazz: buildMaterialItemDisplayData(composition.hdpe_jazz, Operand.hdpe_jazz, instructions),
    hdpeNaturalFoodGrade: buildMaterialItemDisplayData(
      composition.hdpe_natural_food_grade,
      Operand.hdpe_natural_food_grade,
      instructions
    ),
    hdpeNaturalNonFoodGrade: buildMaterialItemDisplayData(
      composition.hdpe_natural_non_food_grade,
      Operand.hdpe_natural_non_food_grade,
      instructions
    ),
    hdpeSiliconTubes: buildMaterialItemDisplayData(
      composition.hdpe_silicon_tubes,
      Operand.hdpe_silicon_tubes,
      instructions
    ),
    hdpeWhiteFoodGrade: buildMaterialItemDisplayData(
      composition.hdpe_white_food_grade,
      Operand.hdpe_white_food_grade,
      instructions
    ),
    hdpeWhiteNonFoodGrade: buildMaterialItemDisplayData(
      composition.hdpe_white_non_food_grade,
      Operand.hdpe_white_non_food_grade,
      instructions
    ),
    metal: buildMaterialItemDisplayData(composition.metal, Operand.metal, instructions),
    otherPlastic: buildMaterialItemDisplayData(composition.other_plastic, Operand.other_plastic, instructions),
    pet: buildMaterialItemDisplayData(composition.pet, Operand.pet, instructions),
    prohibitive: buildMaterialItemDisplayData(composition.prohibitive, Operand.prohibitive, instructions),
    totalHdpe: {
      label: translate('backlog.breakdowns.materialLabels.hdpe', { namespace: 'features' }),
      value: formatPercentageFeature(totalHdpe),
    },
    totalNonHdpe: {
      label: translate('backlog.breakdowns.materialLabels.nonHdpe', { namespace: 'features' }),
      value: formatPercentageFeature(totalNonHdpe),
    },
    waste: buildMaterialItemDisplayData(composition.waste, Operand.waste, instructions),
  };

  const targetHdpeItems: CompositionItem[] = [];
  const nonTargetItems: CompositionItem[] = [];

  targetHdpeItems.push({
    dataTestId: 'material-breakdown-hdpe',
    label: componentDisplayData.totalHdpe.label,
    value: componentDisplayData.totalHdpe.value,
    variant: 'bold',
  });

  if (shouldShowItem(componentDisplayData.hdpeNaturalFoodGrade)) {
    targetHdpeItems.push({
      dataTestId: 'material-breakdown-hdpe-natural-food-grade',
      highlight: componentDisplayData.hdpeNaturalFoodGrade?.highlight,
      label: translate('backlog.breakdowns.materialLabels.hdpeNaturalFoodGrade', { namespace: 'features' }),
      value: componentDisplayData.hdpeNaturalFoodGrade?.value,
    });
  }

  if (shouldShowItem(componentDisplayData.hdpeNaturalNonFoodGrade)) {
    targetHdpeItems.push({
      dataTestId: 'material-breakdown-hdpe-natural-non-food-grade',
      highlight: componentDisplayData.hdpeNaturalNonFoodGrade?.highlight,
      label: translate('backlog.breakdowns.materialLabels.hdpeNaturalNonFoodGrade', { namespace: 'features' }),
      value: componentDisplayData.hdpeNaturalNonFoodGrade?.value,
    });
  }

  if (shouldShowItem(componentDisplayData.hdpeWhiteFoodGrade)) {
    targetHdpeItems.push({
      dataTestId: 'material-breakdown-hdpe-white-food-grade',
      highlight: componentDisplayData.hdpeWhiteFoodGrade?.highlight,
      label: translate('backlog.breakdowns.materialLabels.hdpeWhiteFoodGrade', { namespace: 'features' }),
      value: componentDisplayData.hdpeWhiteFoodGrade?.value,
    });
  }

  if (shouldShowItem(componentDisplayData.hdpeWhiteNonFoodGrade)) {
    targetHdpeItems.push({
      dataTestId: 'material-breakdown-hdpe-white-non-food-grade',
      highlight: componentDisplayData.hdpeWhiteNonFoodGrade?.highlight,
      label: translate('backlog.breakdowns.materialLabels.hdpeWhiteNonFoodGrade', { namespace: 'features' }),
      value: componentDisplayData.hdpeWhiteNonFoodGrade?.value,
    });
  }

  if (shouldShowItem(componentDisplayData.hdpeJazz)) {
    targetHdpeItems.push({
      dataTestId: 'material-breakdown-hdpe-jazz',
      highlight: componentDisplayData.hdpeJazz?.highlight,
      label: translate('backlog.breakdowns.materialLabels.hdpeJazz', { namespace: 'features' }),
      value: componentDisplayData.hdpeJazz?.value,
    });
  }

  if (shouldShowItem(componentDisplayData.hdpeSiliconTubes)) {
    targetHdpeItems.push({
      dataTestId: 'material-breakdown-hdpe-silicon-tubes',
      highlight: componentDisplayData.hdpeSiliconTubes?.highlight,
      label: translate('backlog.breakdowns.materialLabels.hdpeSiliconTubes', { namespace: 'features' }),
      value: componentDisplayData.hdpeSiliconTubes?.value,
    });
  }

  nonTargetItems.push({
    dataTestId: 'material-breakdown-non-hdpe',
    label: componentDisplayData.totalNonHdpe.label,
    value: componentDisplayData.totalNonHdpe.value,
    variant: 'bold',
  });

  if (shouldShowItem(componentDisplayData.expandedPolystyrene)) {
    nonTargetItems.push({
      dataTestId: 'material-breakdown-expanded-polystyrene',
      highlight: componentDisplayData.expandedPolystyrene?.highlight,
      label: translate('backlog.breakdowns.materialLabels.expandedPolystyrene', { namespace: 'features' }),
      value: componentDisplayData.expandedPolystyrene?.value,
    });
  }

  if (shouldShowItem(componentDisplayData.pet)) {
    nonTargetItems.push({
      dataTestId: 'material-breakdown-pet',
      highlight: componentDisplayData.pet?.highlight,
      label: translate('backlog.breakdowns.materialLabels.pet', { namespace: 'features' }),
      value: componentDisplayData.pet?.value,
    });
  }

  if (shouldShowItem(componentDisplayData.otherPlastic)) {
    nonTargetItems.push({
      dataTestId: 'material-breakdown-other-plastic',
      highlight: componentDisplayData.otherPlastic?.highlight,
      label: translate('backlog.breakdowns.materialLabels.otherPlastic', { namespace: 'features' }),
      value: componentDisplayData.otherPlastic?.value,
    });
  }

  if (shouldShowItem(componentDisplayData.metal)) {
    nonTargetItems.push({
      dataTestId: 'material-breakdown-metal',
      highlight: componentDisplayData.metal?.highlight,
      label: translate('backlog.breakdowns.materialLabels.metal', { namespace: 'features' }),
      value: componentDisplayData.metal?.value,
    });
  }

  if (shouldShowItem(componentDisplayData.waste)) {
    nonTargetItems.push({
      dataTestId: 'material-breakdown-waste',
      highlight: componentDisplayData.waste?.highlight,
      label: translate('backlog.breakdowns.materialLabels.waste', { namespace: 'features' }),
      value: componentDisplayData.waste?.value,
    });
  }

  if (shouldShowItem(componentDisplayData.prohibitive)) {
    nonTargetItems.push({
      dataTestId: 'material-breakdown-prohibitive',
      highlight: componentDisplayData.prohibitive?.highlight,
      label: translate('backlog.breakdowns.materialLabels.prohibitive', { namespace: 'features' }),
      value: componentDisplayData.prohibitive?.value,
    });
  }

  return {
    nonTargetSection: {
      items: nonTargetItems,
    },
    targetHdpeSection: {
      items: targetHdpeItems,
    },
  };
};
